import { PauseCircle, Send, Trash2 } from 'lucide-react';

import { ApiError } from '@/api/client';
import type { Vacancy, VacancyAction } from '@/api/schema';
import { Alert } from '@/components/feedback/Alert';
import { useToast } from '@/components/feedback/Toast';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useVacancyAction } from '../api';
import styles from '../pages/CareersPage.module.css';
import { VacancyIconButton } from './VacancyIconButton';

const ACTIONS = {
  deactivate: {
    label: 'Deactivate',
    icon: PauseCircle,
    description:
      'This vacancy will stop accepting applications and disappear from the public Jobs page. You can publish it again later. Existing applications are kept.',
    success: 'Vacancy deactivated.',
  },
  publish: {
    label: 'Publish',
    icon: Send,
    description: 'This vacancy will appear on the public Jobs page and accept applications.',
    success: 'Vacancy published.',
  },
  delete: {
    label: 'Delete',
    icon: Trash2,
    description:
      'This vacancy will be removed from the vacancy list and public Jobs page. Existing applications and candidate records will be kept.',
    success: 'Vacancy deleted.',
  },
} as const;

export function VacancyActions({
  job,
  onSelect,
}: {
  job: Vacancy;
  onSelect: (action: VacancyAction['action']) => void;
}) {
  return (
    <div className={styles.managementActions}>
      {(job.actions ?? []).map(({ action, allowed, reason }) => {
        const { label, icon: Icon } = ACTIONS[action];
        return (
          <div key={action}>
            <VacancyIconButton
              label={label}
              danger={action === 'delete'}
              icon={<Icon size={18} aria-hidden="true" />}
              disabled={!allowed}
              onClick={() => {
                onSelect(action);
              }}
            />
            {reason ? <p className={styles.actionReason}>{reason}</p> : null}
          </div>
        );
      })}
    </div>
  );
}

export function VacancyActionDialog({
  job,
  action,
  onClose,
  onSuccess,
}: {
  job: Vacancy;
  action: VacancyAction['action'];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const mutation = useVacancyAction();
  const toast = useToast();
  const close = () => {
    if (!mutation.isPending) onClose();
  };
  return (
    <Modal
      open
      title={`${ACTIONS[action].label} “${job.title}”?`}
      description={ACTIONS[action].description}
      tone={action === 'delete' ? 'danger' : 'primary'}
      onClose={close}
      footer={
        <>
          <Button variant="ghost" disabled={mutation.isPending} onClick={close}>
            Cancel
          </Button>
          <Button
            variant={action === 'delete' ? 'danger' : 'primary'}
            loading={mutation.isPending}
            onClick={() => {
              mutation.mutate(
                { id: job.id, action },
                {
                  onSuccess: () => {
                    toast.success(ACTIONS[action].success);
                    onSuccess();
                    onClose();
                  },
                },
              );
            }}
          >
            {ACTIONS[action].label} vacancy
          </Button>
        </>
      }
    >
      {mutation.isError ? (
        <Alert tone="danger">
          {mutation.error instanceof ApiError
            ? mutation.error.message
            : 'The vacancy could not be updated. Please try again.'}
        </Alert>
      ) : null}
    </Modal>
  );
}
